import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, MousePointerClick, QrCode, Inbox } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const RANGES = [{ v: 7, l: "7 days" }, { v: 30, l: "30 days" }, { v: 90, l: "90 days" }];

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-[#5C5856]">{label}</span>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="text-3xl font-bold text-[#2A2626] mt-2">{value ?? 0}</p>
  </Card>
);

export function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/analytics?days=${days}`); setData(res.data); }
    catch { /* noop */ } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const series = (data?.series || []).map((d) => ({
    day: d.day.slice(5).replace("-", "/"),
    Views: d.kiosk_view, Taps: d.button_tap, Responses: d.form_submit,
  }));
  const t = data?.totals || {};
  const hasActivity = (t.views || 0) + (t.taps || 0) + (t.qr_scans || 0) + (t.submissions || 0) > 0;

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5C5856]">Engagement on your kiosk over the selected period.</p>
        <div className="flex gap-1 bg-[#F0EBE1] rounded-full p-1">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} className={`px-3 py-1 rounded-full text-sm transition-colors ${days === r.v ? "bg-white text-[#2A2626] shadow-sm" : "text-[#5C5856]"}`} data-testid={`analytics-range-${r.v}`}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Kiosk views" value={t.views} color="#3B7A8C" testid="analytics-views" />
        <StatCard icon={MousePointerClick} label="Button taps" value={t.taps} color="#2A2626" testid="analytics-taps" />
        <StatCard icon={QrCode} label="QR opens" value={t.qr_scans} color="#7A6B57" testid="analytics-qr" />
        <StatCard icon={Inbox} label="Connect Card responses" value={t.submissions} color="#5C7A4B" testid="analytics-submissions" />
      </div>

      <Card className="p-5">
        <h3 className="font-semibold mb-4">Activity over time</h3>
        {loading ? (
          <div className="h-72 flex items-center justify-center text-[#5C5856]">Loading…</div>
        ) : !hasActivity ? (
          <div className="h-72 flex flex-col items-center justify-center text-[#5C5856] text-center">
            <p>No activity yet.</p>
            <p className="text-sm mt-1">Open your kiosk and tap around — data shows up here in real time.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={288}>
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gTaps" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2A2626" stopOpacity={0.35} /><stop offset="100%" stopColor="#2A2626" stopOpacity={0} /></linearGradient>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B7A8C" stopOpacity={0.3} /><stop offset="100%" stopColor="#3B7A8C" stopOpacity={0} /></linearGradient>
                <linearGradient id="gSubs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#5C7A4B" stopOpacity={0.3} /><stop offset="100%" stopColor="#5C7A4B" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#Eee" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#5C5856" }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis tick={{ fontSize: 12, fill: "#5C5856" }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="Views" stroke="#3B7A8C" fill="url(#gViews)" strokeWidth={2} />
              <Area type="monotone" dataKey="Taps" stroke="#2A2626" fill="url(#gTaps)" strokeWidth={2} />
              <Area type="monotone" dataKey="Responses" stroke="#5C7A4B" fill="url(#gSubs)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3">Top buttons</h3>
        {(data?.top_buttons || []).length === 0 ? (
          <p className="text-sm text-[#5C5856]">No button taps yet.</p>
        ) : (
          <div className="space-y-2" data-testid="analytics-top-buttons">
            {data.top_buttons.map((b, i) => {
              const max = data.top_buttons[0].count || 1;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-40 truncate text-sm text-[#2A2626]">{b.label}</span>
                  <div className="flex-1 h-3 rounded-full bg-[#F0EBE1] overflow-hidden">
                    <div className="h-full bg-[#2A2626] rounded-full" style={{ width: `${(b.count / max) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right text-sm font-medium">{b.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
